import dayjs from 'dayjs';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppointmentsStore } from '@/stores/appointments';
import { useCallRequestsStore } from '@/stores/callRequests';
import { useInstitutionsStore } from '@/stores/institutions';
import { useListStore } from '@/stores/list';
import { useToastStore } from '@/stores/toast';
import { useUsersStore } from '@/stores/users';
import { ECallRequestStatus, TCallRequestCamel } from '@/types';
import {
  calculateAge,
  getTimeFormat,
  getTimeZoneOffsetInMinutes,
} from '@/utils/convertDate';
import { nameFormat } from '@/utils/valuesFormat';

/**
 * Handles a presenter's call delivered over the notifications socket: keeps
 * the calls store up to date and, once the call is over, tells its ends how
 * it went. A declined or missed call is shown to both ends, an accepted one
 * to the presenter: the callee, a provider or a presenter, waits in the
 * session's Waiting Room, or joins the session right away when it has none.
 * @returns The handler of a `call_request` notification.
 */
export const useCallRequestNotification = () => {
  const { t } = useTranslation();
  const applyCallRequest = useCallRequestsStore(
    (state) => state.applyCallRequest,
  );
  const getAppointment = useAppointmentsStore((state) => state.getAppointment);
  const getService = useInstitutionsStore((state) => state.getService);
  const { checkRoleIds, userTimezone } = useUsersStore((state) => ({
    checkRoleIds: state.checkRoleIds,
    userTimezone: state.userData?.timezone,
  }));
  const timezones = useListStore((state) => state.timezones);
  const setToast = useToastStore((state) => state.setToast);

  return useCallback(
    async (callRequest: TCallRequestCamel) => {
      applyCallRequest(callRequest);

      const { status, callee, appointmentId } = callRequest;

      if (status === ECallRequestStatus.ACCEPTED) {
        if (!checkRoleIds([callRequest.callerUserInstitutionRoleId])) return;
        const appointment = await getAppointment(appointmentId).catch(
          (error: unknown) => {
            console.error(error);
            return undefined;
          },
        );
        setToast({
          title: t('call-request.accepted'),
          description: t(
            appointment?.waitingRoom?.id
              ? 'call-request.joined-waiting-room'
              : 'call-request.joined-session',
            { name: nameFormat(callee?.firstName, callee?.lastName) },
          ),
          variant: 'success',
        });
        return;
      }
      if (
        status !== ECallRequestStatus.DECLINED &&
        status !== ECallRequestStatus.EXPIRED
      )
        return;

      const patient = callRequest.appointment?.patient;
      const service =
        getService(callRequest.appointment?.institutionServiceId ?? '')?.name ??
        t('dashboard.unknown-service');
      const patientName =
        patient &&
        (patient.dob
          ? t('call-request.patient-age', {
              name: nameFormat(patient.firstName, patient.lastName),
              age: calculateAge(patient.dob),
            })
          : nameFormat(patient.firstName, patient.lastName));
      const expiresAt = dayjs(callRequest.expiresAt);
      const offset = getTimeZoneOffsetInMinutes(
        callee?.timezone ?? userTimezone,
        timezones,
        expiresAt,
      );
      setToast({
        title:
          status === ECallRequestStatus.DECLINED
            ? t('call-request.declined')
            : t('call-request.missed'),
        description: (
          <>
            {patientName ? `${service}: ${patientName}` : service}
            <br />
            {getTimeFormat(expiresAt.utcOffset(offset))}
          </>
        ),
        variant: status === ECallRequestStatus.DECLINED ? 'warning' : 'error',
      });
    },
    [
      applyCallRequest,
      checkRoleIds,
      getAppointment,
      getService,
      setToast,
      t,
      timezones,
      userTimezone,
    ],
  );
};
